import { Theme, Users } from "@just-in-types";
import { differenceInYears } from "date-fns";
import * as React from "react";
import styled from "react-emotion";
import { connect } from "react-redux";
import Button from "../components/Button";
import Column from "../components/Column";
import Container from "../components/Container";
import NextButton from "../components/NextButton";
import ResetButton from "../components/ResetButton";
import Row from "../components/Row";
import BaseSelect from "../components/Select";
import BaseText from "../components/Text";
import Title from "../components/Title";
import ErrorBoundary from "../hoc/withErrorBoundary";
import { RouteComponentProps } from "../Site";
import { navigateToNext, NavigateToNext } from "../store/actions";
import { IStrings } from "../store/reducers/strings";

const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/*
  TODO:
  1) Translate month names to Spanish
  2) Add error when incorrect date is selected
  3) Translate "Select {month/day}"
*/

const getYears = (businessId) => {
  const today = new Date();
  const maxYear = today.getFullYear();
  const minYear = maxYear - 122;

  let minAge = 18;
  if (businessId === "3DmMhzu7mCa3hpLUEbp9") {
    // U Pull U Save
    minAge = 16;
  }
  const yearDefault = maxYear - minAge;
  const years = [];

  for (let year = minYear; year <= maxYear; year++) {
    years.push(year);
  }

  return { yearDefault, years };
};

const getDays = (monthString: string, yearString: string) => {
  const month = parseInt(monthString, 10);
  const year = parseInt(yearString, 10);
  const lastDay = new Date(year, month, 0).getDate();
  const days = [];

  for (let day = 1; day <= lastDay; day++) {
    days.push(day);
  }

  return days;
};

interface IDateOfBirthDispatchProps {
  onNavigateToNext: NavigateToNext;
}
interface IDateOfBirthStateProps {
  languageStrings: IStrings;
  theme: Theme.Props;
  user: Users.User;
}
interface IDateOfBirthOwnProps extends RouteComponentProps {
  businessId: string;
  siteId: string;
}
type DateOfBirthProps = RouteComponentProps & IDateOfBirthDispatchProps & IDateOfBirthStateProps;
interface IDateOfBirthState {
  sending: boolean;
  day: string;
  days: JSX.Element[];
  month: string;
  months: JSX.Element[];
  year: string;
  years: JSX.Element[];
  dateOfBirth: null | Date;
  dateOfBirthIsValid: boolean;
}
type DateOfBirthState = IDateOfBirthState;

const DateOfBirthContainer = styled(Container)``;
const Select = styled(BaseSelect)`
  background-color: white;
  font-size: 2rem;
  margin: 0 0.5rem;
  min-width: 6em;
`;
const SelectContainer = styled("div")`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  grid-template-rows: 1fr 1fr;
  grid-column-gap: 1rem;
`;
const Text = styled(BaseText)`
  font-size: 2.5rem;
  font-weight: bold;
  padding-left: 0.5rem;
`;
const Warning = styled(Title)`
  color: ${(props: { color: string; isValid: boolean }) => props.color};
  opacity: ${(props) => (props.isValid ? 0 : 1)};
  margin: 1rem auto;
  font-size: 2rem;
  transition: opacity 250 ease-in-out;
`;

class DateOfBirth extends React.Component<DateOfBirthProps, DateOfBirthState> {
  constructor(props: DateOfBirthProps) {
    super(props);

    const { yearDefault, years } = getYears(this.props.businessId);
    const Months = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((month, index) => (
      <option key={index} value={month}>
        {monthNames[month - 1]}
      </option>
    ));

    const Years = years.map((year, index) => (
      <option key={index} value={year}>
        {year}
      </option>
    ));

    this.state = {
      dateOfBirth: null,
      dateOfBirthIsValid: false,
      day: "",
      days: [],
      month: "",
      months: Months,
      sending: false,
      year: yearDefault.toString(),
      years: Years,
    };
  }

  public componentDidMount() {
    const {
      onNavigateToNext,
      user: { dateofbirth },
    } = this.props;

    const search = window.location.search;
    const params = new URLSearchParams(search);
    const from = params.get("from");

    if (dateofbirth && from !== "thanks") {
      onNavigateToNext();
    }
  }

  public render() {
    const {
      languageStrings: strings,
      theme: { colors },
      site: { kioskUiBackgroundColor, kioskUiTitleTextColor, kioskUiTextColor, kioskUiButtonSuccessColor },
      site,
      businessId,
    } = this.props;
    const { dateOfBirth, dateOfBirthIsValid, day, days, month, months, year, years } = this.state;
    let minAge = 18;
    if (businessId === "3DmMhzu7mCa3hpLUEbp9") {
      // U Pull U Save
      minAge = 16;
    }

    return (
      <ErrorBoundary>
        <DateOfBirthContainer style={{ background: kioskUiBackgroundColor }}>
          <Row align="center" justify="center">
            <Title color={kioskUiTitleTextColor ? kioskUiTitleTextColor : colors.secondary}>
              {strings.datOfBirthHeader}
            </Title>
          </Row>
          <Column align="center" justify="space-around">
            <Row>
              <Column align="center">
                <SelectContainer>
                  <Text color={kioskUiTextColor ? kioskUiTextColor : colors.warning}>{strings.month}</Text>
                  <Text color={kioskUiTextColor ? kioskUiTextColor : colors.warning}>{strings.day}</Text>
                  <Text color={kioskUiTextColor ? kioskUiTextColor : colors.warning}>{strings.year}</Text>
                  <Select disabled={!year} name="month" onChange={this.handleMonth} value={month}>
                    <option value="null">Select Month</option>
                    {months}
                  </Select>
                  <Select disabled={!month || !year} name="day" onChange={this.handleDay} value={day}>
                    <option value="null">Select Day</option>
                    {days}
                  </Select>
                  <Select name="year" onChange={this.handleYear} value={year}>
                    {years}
                  </Select>
                </SelectContainer>
                <Warning color={colors.error} isValid={dateOfBirthIsValid}>
                  Must be {minAge} years or older to enter.
                </Warning>
              </Column>
            </Row>
          </Column>
          <Row align="center" justify="center">
            <ResetButton site={site} />
            <NextButton
              bg={kioskUiButtonSuccessColor ? kioskUiButtonSuccessColor : colors.success}
              disabled={!dateOfBirthIsValid}
              name="dateofbirth"
              value={dateOfBirth}
            >
              {strings.addDateOfBirth}
            </NextButton>
          </Row>
        </DateOfBirthContainer>
      </ErrorBoundary>
    );
  }

  private handleYear = ({ currentTarget: { value: year } }: React.FormEvent<HTMLSelectElement>) =>
    this.setState({ year }, this.processDateOfBirth);

  private handleMonth = ({ currentTarget: { value: month } }: React.FormEvent<HTMLSelectElement>) =>
    this.setState({ month }, this.processDateOfBirth);

  private generateDays = () => {
    const { month, year } = this.state;

    if (month && year) {
      const days = getDays(month, year).map((day, index) => (
        <option key={index} value={day}>
          {day}
        </option>
      ));
      this.setState({ days });
    }
  };

  private handleDay = ({ currentTarget: { value: day } }: React.FormEvent<HTMLSelectElement>) =>
    this.setState({ day }, this.processDateOfBirth);

  private validateDateOfBirth = ({ day, month, year }: { day: string; month: string; year: string }) => {
    if (!day || !month || day === "null" || month === "null") {
      return false;
    }

    const dateOfBirth = new Date(`${month}/${day}/${year}`);
    const today = new Date();
    const difference = differenceInYears(today, dateOfBirth);
    let minAge = 18;
    const businessId = this.props.businessId;
    if (businessId === "3DmMhzu7mCa3hpLUEbp9") {
      // u pull u save
      minAge = 16;
    }

    if (difference >= minAge) {
      return true;
    } else {
      return false;
    }
  };

  private processDateOfBirth = () => {
    const { day, month, year } = this.state;

    this.generateDays();

    const dateOfBirthIsValid = this.validateDateOfBirth({ day, month, year });

    this.validateDateOfBirth({ day, month, year });

    if (dateOfBirthIsValid) {
      const dateofbirth = new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10));
      this.setState({ dateOfBirthIsValid: true, dateOfBirth: dateofbirth });
    } else {
      this.setState({ dateOfBirthIsValid: false });
    }
  };
}

const mapStateToProps = ({ router: { languageStrings }, theme, user }) => ({
  languageStrings,
  theme,
  user,
});

const mapDispatchToProps = {
  onNavigateToNext: navigateToNext,
};

export default connect<IDateOfBirthStateProps, IDateOfBirthDispatchProps, IDateOfBirthOwnProps>(
  mapStateToProps,
  mapDispatchToProps
)(DateOfBirth);



// WEBPACK FOOTER //
// ./src/views/DateOfBirth.tsx